<template>
  <div class="processor-pipeline">
    <!-- Fullscreen loader overlay -->
    <div class="processor-pipeline__loader-container" v-if="loading">
      <loader size="large" type="infinite-spinner"></loader>
    </div>

    <!-- Advanced Filter Modal -->
    <div v-if="showFilterModal" class="adv-filter-overlay" @click.self="showFilterModal = false">
        <div class="adv-filter-modal">
          <div class="adv-filter-title">Advanced filters</div>

          <div class="adv-filter-rows">
            <div
              v-for="(row, i) in advancedFilters"
              :key="i"
              class="adv-filter-row"
            >
              <button
                v-if="i > 0"
                class="logic-badge"
                :class="row.logic === 'AND' ? 'logic-and' : 'logic-or'"
                @click="row.logic = row.logic === 'AND' ? 'OR' : 'AND'"
              >{{ row.logic }}</button>
              <div v-else class="logic-spacer"></div>

              <select v-model="row.field" class="adv-select adv-select--field">
                <option v-for="col in filterableColumns" :key="col.field" :value="col.field">
                  {{ col.title }}
                </option>
              </select>

              <select v-model="row.operator" class="adv-select adv-select--op">
                <option value="contains">contains</option>
                <option value="eq">equals</option>
                <option value="neq">not equals</option>
                <option value="startswith">starts with</option>
                <option value="isempty">is empty</option>
                <option value="isnotempty">is not empty</option>
              </select>

              <input
                v-if="!['isempty','isnotempty'].includes(row.operator)"
                v-model="row.value"
                class="adv-input"
                placeholder="Value..."
              />
              <div v-else class="adv-input adv-input--placeholder"></div>

              <button class="adv-rm-btn" @click="advancedFilters.splice(i, 1)">×</button>
            </div>
          </div>

          <button class="adv-add-btn" @click="addFilterRow">+ Add condition</button>

          <div class="adv-modal-actions">
            <button class="adv-clear-btn" @click="clearAdvancedFilters">Clear all filters</button>
            <button class="adv-cancel-btn" @click="showFilterModal = false">Cancel</button>
            <button class="adv-apply-btn" @click="applyAdvancedFilters">Apply filters</button>
          </div>
        </div>
      </div>

    <div class="processor-pipeline__container">
      <!-- Sub-bar: My Loans / All Loans -->
      <div class="sub-toolbar">
        <div class="mine-segment">
          <button
            class="mine-segment__btn"
            :class="{ active: isMine }"
            @click="isMine !== true && toggleIsMine()"
          >My Loans</button>
          <button
            class="mine-segment__btn"
            :class="{ active: !isMine }"
            @click="isMine !== false && toggleIsMine()"
          >All Loans</button>
        </div>
      </div>

      <CommandCenterTable
        v-if="columnsLoaded"
          ref="ccTable"
        :columns="tableColumns"
        :data="gridItems"
        :server-side="true"
        :server-total="total"
        :server-skip="skip"
        :default-page-size="take"
        :max-page-size="100"
        :numbered-pager="numberedPager"
        :show-header="false"
        :filterable="true"
        selected-field="selected"
        empty-text="No records found."
        @page-change="onPageChange"
        @sort="onTableSort"
        @filter-change="onColumnFilterChange"
        @row-click="onRowClick"
      >
        <template #toolbar>
          <button
            class="gear-btn"
            :class="{ 'has-filters': advancedFilters.length }"
            @click="showFilterModal = true"
            title="Advanced Filtering"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="3"></circle>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
            </svg>
            <span v-if="advancedFilters.length" class="filter-count-badge">{{ advancedFilters.length }}</span>
          </button>

          <div class="view-container">
            <select class="cc-view-select" v-model="selectedViewValue" @change="onViewChange">
              <option v-for="v in views" :key="v.value" :value="v.value">{{ v.text }}</option>
            </select>
          </div>
          <span class="view-description">{{ selectedView.desc }}</span>

          <div class="search-bar-container">
            <input
              class="cc-search-input"
              type="text"
              placeholder="Search..."
              :value="searchWord"
              @input="onFilter"
            />
            <button class="clear-button" @click="clearSearch">Clear</button>
          </div>
        </template>

        <template #[`filterHeader:acceptedtasks`]="{ col, sort: activeSort }">
          <div class="tasks-header-cell" @click.stop>
            <div class="header-summary header-summary--tight">
              <svg class="header-pie" viewBox="0 0 32 32" width="28" height="28">
                <circle r="14" cx="16" cy="16" fill="transparent" stroke="#e0e0e0" stroke-width="4" />
                <circle
                  v-for="seg in pieSegments"
                  :key="seg.color"
                  r="14" cx="16" cy="16"
                  fill="transparent"
                  :stroke="seg.color"
                  stroke-width="4"
                  :stroke-dasharray="`${seg.dash} ${seg.gap}`"
                  :stroke-dashoffset="seg.offset"
                  style="transition: stroke-dasharray 0.3s ease;"
                />
              </svg>
              <div class="header-pills">
                <div @click="headerClicked('Accepted')" class="summary-pill ok" title="Accepted">
                  <span class="pill-icon">✅</span>
                  <span class="pill-num">{{ acceptedSum }}</span>
                </div>
                <div @click="headerClicked('Pending')" class="summary-pill warn" title="Pending">
                  <span class="pill-icon">⏳</span>
                  <span class="pill-num">{{ pendingSum }}</span>
                </div>
                <div @click="headerClicked('Outstanding')" class="summary-pill danger" title="Outstanding">
                  <span class="pill-icon">❌</span>
                  <span class="pill-num">{{ rejectedSum }}</span>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template #acceptedTaskCell="{ dataItem }">
          <div v-if="allAccepted(dataItem)" class="task-pill celebratory" title="All tasks accepted">
            All Accepted
          </div>
          <div v-else-if="noTasks(dataItem)" class="task-pill neutral" title="No tasks">
            No Tasks
          </div>
          <div v-else class="task-pills" role="group" aria-label="Task counts">
            <div class="task-pill ok" title="Accepted">
              <span class="pill-icon">✅</span>
              <span class="pill-num">{{ toNum(dataItem.acceptedtasks) }}</span>
            </div>
            <div class="task-pill warn" title="Pending">
              <span class="pill-icon">⏳</span>
              <span class="pill-num">{{ toNum(dataItem.pendingtasks) }}</span>
            </div>
            <div class="task-pill danger" title="Rejected">
              <span class="pill-icon">❌</span>
              <span class="pill-num">{{ toNum(dataItem.rejectedtasks) }}</span>
            </div>
          </div>
        </template>

        <!-- Dynamic date cell — renders whichever field is active for this view -->
        <template #dynamicDateCell="{ dataItem }">
          {{ dateFormatter(dataItem[dynamicDateField]) }}
        </template>

        <template #addressCell="{ dataItem }">
          <span style="word-wrap: break-word; font-size: .9rem;">{{ dataItem.f11 }}</span>
        </template>
        <template #clientCell="{ dataItem }">
          <span style="word-wrap: break-word; font-size: .9rem;">{{ dataItem.borrowername }}</span>
        </template>
        <template #loanCell="{ dataItem }">
          <span style="word-wrap: break-word; font-size: .9rem;">{{ dataItem.f364 }}</span>
        </template>

        <!-- Lock Mode launch for this loan — opens Polly's Pricer UI in a right-side flyout
             over the grid, so the user doesn’t lose their scroll position or filters. -->
        <template #pollyPricerCell="{ dataItem }">
          <button
            type="button"
            class="polly-pricer-btn"
            title="Price this loan in the Pricing Engine"
            @click.stop="openPollyPricer(dataItem)"
          >
            <i class="fa-light fa-calculator"></i> Price
          </button>
        </template>
      </CommandCenterTable>

      <PollyPricerFlyout
        :open="pollyFlyoutOpen"
        :loan-id="pollyFlyoutLoanId"
        :org-ticker="pollyFlyoutOrgTicker"
        @close="closePollyPricer"
      />

    </div>
  </div>
</template>

<script>
import { Loader } from "@progress/kendo-vue-indicators";
import CommandCenterTable from './commandCenter/CommandCenterTable.vue';
import { isConnectEnabled } from '@/helpers/connect/connectFeatures.js';
import PollyPricerFlyout from './commandCenter/PollyPricerFlyout.vue';
import apiService from '../api/api.service.js';
import auth from '../authConfig.js';

// Default sort applied when switching views
const VIEW_DEFAULTS = {
  'All':          [{ field: 'estimatedclosingdate',      dir: 'asc'  }],
  'Prospects':    [{ field: 'f745',                      dir: 'asc'  }], // Started Date
  'Pipeline':     [{ field: 'estimatedclosingdate',      dir: 'asc'  }], // Est. Closing
  'Post Closing': [{ field: 'f749',                      dir: 'desc' }], // Status Date
  'Closed Loans': [{ field: 'ms_fun',                    dir: 'desc' }], // Funded Date
  'Aged Loans':   [{ field: 'lastcompletedmilestonedate',dir: 'asc'  }],
  'Aged Credit':  [{ field: 'creditreportorderdate',     dir: 'asc'  }],
  'Non-Active':   [{ field: 'estimatedclosingdate',      dir: 'desc' }],
};

// Dynamic date column — title and backing field per view
const VIEW_DATE_COL = {
  'Prospects':    { field: 'f745',   title: 'Started Date' },
  'Post Closing': { field: 'f749',   title: 'Status Date'  },
  'Closed Loans': { field: 'ms_fun', title: 'Funded Date'  },
};

export default {
  props: ['queryType', 'sessionType', 'title'],

  components: {
    CommandCenterTable,
    PollyPricerFlyout,
    'loader': Loader,
  },

  data() {
    return {
      numberedPager: isConnectEnabled(), // Contacts-style pager with the Connect skin (views/ProcessingV2.vue)
      columnsLoaded:    false,
      loading:          false,
      searchWord:       '',
      selectedViewValue: 'All',
      windowWidth:      window.innerWidth,

      // Left-side Pricing Engine flyout, launched from a Pipeline row (see openPollyPricer).
      pollyFlyoutOpen:      false,
      pollyFlyoutLoanId:    null,
      pollyFlyoutOrgTicker: null,

      isMine: JSON.parse(localStorage.getItem('pipeline_is_mine') ?? 'true'),

      showFilterModal:      false,
      advancedFilters:      [],
      _advancedFilterGroup: null,

      gridItems:   [],
      total:       0,
      acceptedSum: 0,
      pendingSum:  0,
      rejectedSum: 0,

      skip:  0,
      take:  50,
      sort:  [],
      filter: null,
      searchFilter: null,

      loanType:    'Loan',
      azureId:     '',
      _debounceId: null,
      DEBOUNCE_MS: 800,

      // Dropdown order per request
      views: [
        { text: "All Loans",           value: "All",          desc: "Every file regardless of status" },
        { text: "Prospects",           value: "Prospects",    desc: "Active files pending disclosure" },
        { text: "Pipeline",            value: "Pipeline",     desc: "Active files that have been disclosed" },
        { text: "Post Closing",        value: "Post Closing", desc: "Files currently in Post Closing or Note Delivery" },
        { text: "Closed Loans",        value: "Closed Loans", desc: "All funded loans" },
        { text: "Aged Loans",          value: "Aged Loans",   desc: "Files that have not moved milestone in 20 days or more" },
        { text: "Aged Credit",         value: "Aged Credit",  desc: "Credit Report is more than 20 days old and no milestone movement" },
        { text: "Withdrawn/Cancelled", value: "Non-Active",   desc: "Files that have been withdrawn or cancelled" },
      ],
    };
  },

  computed: {
    dataItemKey() {
      return this.queryType === this.loanType ? 'id' : 'contact_id';
    },

    selectedView() {
      return this.views.find(v => v.value === this.selectedViewValue) || this.views[0];
    },

    // The real DB field to use for the dynamic date column
    dynamicDateField() {
      return (VIEW_DATE_COL[this.selectedViewValue] || {}).field || 'estimatedclosingdate';
    },

    dynamicDateTitle() {
      return (VIEW_DATE_COL[this.selectedViewValue] || {}).title || 'Est. Closing Date';
    },

    // Columns for the advanced filter builder — always includes Loan Status (f1393)
    filterableColumns() {
      const base = this.tableColumns.filter(
        c => c.field && c.field !== 'acceptedtasks' && c.field !== 'dynamicdate' && c.field !== 'pollypricer'
      );
      const hasLoanStatus = base.some(c => c.field === 'f1393');
      if (!hasLoanStatus) {
        base.push({ field: 'f1393', title: 'Loan Status' });
      }
      return base;
    },

    pieSegments() {
      const total = this.acceptedSum + this.pendingSum + this.rejectedSum;
      if (!total) return [];
      const circ = 2 * Math.PI * 14;
      let offset = 0;
      const startOffset = circ * 0.25;
      const items = [
        { value: this.acceptedSum, color: '#76db7a' },
        { value: this.pendingSum,  color: '#ede76b' },
        { value: this.rejectedSum, color: '#ed766b' },
      ];
      return items.map(item => {
        const pct  = item.value / total;
        const dash = pct * circ;
        const gap  = circ - dash;
        const seg  = { color: item.color, dash, gap, offset: circ - offset + startOffset };
        offset += dash;
        return seg;
      });
    },

    tableColumns() {
      const v = this.selectedViewValue;
      const isClosedOrPostClosing = ['Closed Loans', 'Post Closing'].includes(v);

      if (this.windowWidth <= 430) {
        return [
          { field: "acceptedtasks", title: "Tasks",       cell: "acceptedTaskCell", sortable: false, filterable: false, width: 55 },
          { field: "f364",          title: "Loan Number", cell: "loanCell",         sortable: true,  filterOperator: 'startswith' },
          { field: "borrowername",  title: "Client",      cell: "clientCell",       sortable: true  },
          { field: "f11",           title: "Address",     cell: "addressCell",      sortable: true  },
        ];
      }

      return [
        { field: "acceptedtasks",  title: "Tasks",        cell: "acceptedTaskCell", sortable: false, filterable: false, width: 306 },
        { field: "f364",           title: "Loan Number",  sortable: true,  filterOperator: 'startswith' },
        { field: "borrowername",   title: "Client",       sortable: true  },
        { field: "f11",            title: "Address",      sortable: true  },
        { field: "f317",           title: "Loan Officer", sortable: true  },
        { field: "f2",             title: "Loan Amount",  sortable: true, type: "currency" },
        { field: "f19",            title: "Loan Purpose", sortable: true  },
        isClosedOrPostClosing
          ? { field: "cxlosproducttype", title: "Product",   sortable: true }
          : { field: "f1172",            title: "Loan Type",  sortable: true },
        isClosedOrPostClosing
          ? { field: "losinvestor",      title: "Investor",  sortable: true }
          : { field: "currentmilestone", title: "Milestone", sortable: true },
        // Dynamic date — title and cell data swap per view
        {
          field:      "dynamicdate",
          title:      this.dynamicDateTitle,
          cell:       "dynamicDateCell",
          sortable:   true,
          filterable: true,
          filterOperator: 'gte', type: 'date',
        },
        { field: "orgid", title: "ORGID", sortable: true },
        {
          // No header text — the button itself (and its tooltip) says what it does, and the
          // "Pricer"/"Pricing Engine" label just crowded the header row.
          field:      "pollypricer",
          title:      "",
          cell:       "pollyPricerCell",
          sortable:   false,
          filterable: false,
          width:      90,
        },
      ];
    },
  },

  async mounted() {
    window.addEventListener("resize", this.getScreenWidth);
    this.selectedViewValue = 'Pipeline';
    // Run onViewChange to properly set the filter, sort, and _viewFilter for Pipeline
    this.onViewChange();
    setInterval(() => { this.fetchData(); }, 300000);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.getScreenWidth);
  },

  methods: {
    getScreenWidth() {
      this.windowWidth = window.innerWidth;
    },

    dateFormatter(v) {
      if (!v) return "";
      const d = new Date(v);
      if (isNaN(d)) return v;
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day   = String(d.getDate()).padStart(2, "0");
      return `${month}/${day}/${d.getFullYear()}`;
    },

    toNum(n) {
      const v = Number(n);
      return Number.isFinite(v) ? v : 0;
    },

    noTasks(row) {
      return this.toNum(row?.acceptedtasks) + this.toNum(row?.pendingtasks) + this.toNum(row?.rejectedtasks) === 0;
    },

    allAccepted(row) {
      const a = this.toNum(row?.acceptedtasks);
      const p = this.toNum(row?.pendingtasks);
      const r = this.toNum(row?.rejectedtasks);
      return a > 0 && p === 0 && r === 0;
    },

    toggleIsMine() {
      this.isMine = !this.isMine;
      localStorage.setItem('pipeline_is_mine', JSON.stringify(this.isMine));
      this.skip = 0;
      this.fetchData();
    },

    addFilterRow() {
      const firstCol = this.filterableColumns[0];
      this.advancedFilters.push({
        logic:    'AND',
        field:    firstCol?.field || 'f317',
        operator: 'contains',
        value:    '',
      });
    },

    clearAdvancedFilters() {
      this.advancedFilters      = [];
      this._advancedFilterGroup = null;
      this.showFilterModal      = false;
      this.skip = 0;
      this.fetchData();
    },

    applyAdvancedFilters() {
      this._advancedFilterGroup = this.advancedFilters.length
        ? this._buildFilterGroup(this.advancedFilters)
        : null;
      this.showFilterModal = false;
      this.skip = 0;
      this.fetchData();
    },

    _buildFilterGroup(rows) {
      const orGroups   = [];
      let currentGroup = [];
      rows.forEach((row, i) => {
        if (i > 0 && row.logic === 'OR' && currentGroup.length) {
          orGroups.push({ logic: 'and', filters: currentGroup.map(r => this._rowToFilter(r)) });
          currentGroup = [];
        }
        currentGroup.push(row);
      });
      if (currentGroup.length) {
        orGroups.push({ logic: 'and', filters: currentGroup.map(r => this._rowToFilter(r)) });
      }
      return orGroups.length === 1 ? orGroups[0] : { logic: 'or', filters: orGroups };
    },

    _rowToFilter(row) {
      return { field: row.field, operator: row.operator, value: row.value ?? '' };
    },

    _buildMergedFilter() {
      const parts = [];
      if (this.filter?.filters?.length) parts.push(this.filter);
      if (this._advancedFilterGroup)    parts.push(this._advancedFilterGroup);
      if (!parts.length) return null;
      return parts.length === 1 ? parts[0] : { logic: 'and', filters: parts };
    },

    onColumnFilterChange({ filters }) {
      // Resolve the "dynamicdate" proxy to the real backing field for this view
      const resolved = filters.map(f =>
        f.field === 'dynamicdate' ? { ...f, field: this.dynamicDateField } : f
      );

      // Column header filters search ALL loans — view filter is intentionally excluded.
      // When filters are cleared, restore the active view filter.
      if (!resolved.length) {
        this.filter = this._viewFilter || { logic: 'and', filters: [] };
      } else {
        this.filter = { logic: 'and', filters: resolved };
      }

      this.skip = 0;
      if (this._debounceId) clearTimeout(this._debounceId);
      this._debounceId = setTimeout(() => { this.fetchData(); }, this.DEBOUNCE_MS);
    },

    clearAllSorts() {
      this.sort = [];
      this.skip = 0;
      this.fetchData();
    },

    headerClicked(label) {
      const map = { Accepted: 'acceptedtasks', Pending: 'pendingtasks', Outstanding: 'rejectedtasks' };
      const field = map[label];
      if (!field) return;
      const cur = this.sort[0];
      if (cur && cur.field === field) {
        const d = (cur.dir || '').toLowerCase();
        if      (d === 'desc') this.sort = [{ field, dir: 'asc'  }];
        else if (d === 'asc')  this.sort = [];
        else                   this.sort = [{ field, dir: 'desc' }];
      } else {
        this.sort = [{ field, dir: 'desc' }];
      }
      this.skip = 0;
      this.fetchData();
    },

    onTableSort({ field, dir }) {
      // Resolve the proxy "dynamicdate" column to the real field for this view
      const resolvedField = field === 'dynamicdate' ? this.dynamicDateField : field;
      this.sort = [{ field: resolvedField, dir }];
      this.skip = 0;
      this.fetchData();
    },

    onPageChange({ skip, take }) {
      this.skip = skip;
      this.take = take;
      this.fetchData();
    },

    onRowClick(row) {
      const isDev   = window.location.hostname === 'localhost' || window.location.hostname === 'dev.swiftapp.ai';
      const baseUrl = isDev ? 'https://dev-swift.goldstarfinancial.com' : 'https://swift.goldstarfinancial.com';
      window.open(`${baseUrl}/LoanTasks/?id=${row.id}`, '_blank');
    },

    // Lock Mode: opens Polly's Pricer UI for this specific loan as a right-side flyout over
    // the grid (PollyPricerFlyout.vue), instead of navigating away, so the user keeps their
    // place on the Pipeline. row.id is the same SWIFT loan GUID Polly's Loan Service already
    // keys on (see GetLoanPriceSheet on the backend); row.orgid is that loan's branch org ticker.
    openPollyPricer(row) {
      if (!row?.id) return;
      this.pollyFlyoutLoanId    = row.id;
      this.pollyFlyoutOrgTicker = row.orgid || null;
      this.pollyFlyoutOpen      = true;
    },

    closePollyPricer() {
      this.pollyFlyoutOpen = false;
    },

    async fetchData() {
      const replaceBorrowerSort = (sortArr) => {
        if (!Array.isArray(sortArr) || !sortArr.length) return sortArr;
        const out = [];
        sortArr.forEach(s => {
          if (s.field === "borrowername") {
            out.push({ field: "f4000", dir: s.dir });
            out.push({ field: "f4002", dir: s.dir });
          } else {
            out.push(s);
          }
        });
        return out;
      };

      const replaceBorrower = node => {
        if (!node) return;
        if (Array.isArray(node.filters)) { node.filters.forEach(replaceBorrower); return; }
        if (node.field === "borrowername") {
          const op    = node.operator || "contains";
          const raw   = node.value ?? "";
          const parts = String(raw).trim().split(/\s+/);
          const word1 = parts[0] || "";
          const word2 = (parts.length > 1 ? parts[1] : parts[0]) || "";
          Object.assign(node, {
            logic: parts.length > 1 ? "and" : "or",
            filters: [
              { field: "f4000", operator: op, value: word1 },
              { field: "f4002", operator: op, value: word2 },
            ],
          });
          delete node.field; delete node.operator; delete node.value;
        }
      };

      this.loading = true;
      try {
        const tokenResponse = await auth.acquireTokenResponse();
        this.azureId = localStorage.getItem('azure_id');
        if (!tokenResponse) throw new Error("Failed to acquire token response");

        const mergedFilter = this._buildMergedFilter();

        const dataState = {
          sort:   replaceBorrowerSort(this.sort),
          skip:   this.skip,
          take:   this.take,
          filter: mergedFilter ? JSON.parse(JSON.stringify(mergedFilter)) : null,
          group:  [],
          isMine: this.isMine,
        };

        if (dataState.filter) replaceBorrower(dataState.filter);

        const response = await apiService.GetSwiftAppPipelineV2(
          this.azureId,
          JSON.stringify(dataState),
          tokenResponse.accessToken
        );
        const data = response.data;

        if (data && Array.isArray(data.data.contacts)) {
          this.gridItems   = data.data.contacts;
          this.total       = data.data.total;
          this.acceptedSum = data.data.accepted;
          this.pendingSum  = data.data.pending;
          this.rejectedSum = data.data.rejected;
        } else {
          console.error("No valid data received from API");
          this.gridItems = [];
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        this.loading       = false;
        this.columnsLoaded = true;
      }
    },

    onFilter(e) {
      this.searchWord = e.target.value;

      if (this.searchWord) {
        const filters = this.tableColumns
          .filter(col => col.field !== "dynamicdate")
          .flatMap(col => {
            if (col.field === "borrowername") {
              return [
                { field: "f4000", operator: "contains", value: this.searchWord },
                { field: "f4002", operator: "contains", value: this.searchWord },
              ];
            }
            return { field: col.field, operator: "contains", value: this.searchWord };
          });
        this.searchFilter = { logic: 'or', filters };
      } else {
        this.searchFilter = null;
      }

      this.skip   = 0;
      this.filter = this.searchFilter;

      if (this._debounceId) clearTimeout(this._debounceId);
      this._debounceId = setTimeout(() => { this.fetchData(); }, this.DEBOUNCE_MS);
    },

    onViewChange() {
      this.searchWord = '';
      // Apply default sort for this view
      this.sort = VIEW_DEFAULTS[this.selectedViewValue] || [];
      this.skip = 0;

      const today  = new Date();
      const cutoff = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 20);

      const excludeCsllexclude = {
        logic: 'or',
        filters: [
          { field: 'cxllexclude', operator: 'eq',     value: false },
          { field: 'cxllexclude', operator: 'isnull' },
        ],
      };
      const excludeAdverse = { field: 'loanfolder', operator: 'neq', value: 'Non-App Adverse' };

      switch (this.selectedViewValue) {
        case 'Prospects':
          this.filter = { logic: 'and', filters: [ excludeCsllexclude, { field: 'f1393', operator: 'eq', value: 'Active Loan' }, { field: 'f3152', operator: 'isempty', filter: "date", type: "date" }, excludeAdverse ] };
          break;
        case 'Pipeline':
          this.filter = { logic: 'and', filters: [ excludeCsllexclude, { field: 'f1393', operator: 'eq', value: 'Active Loan' }, { field: 'f3152', operator: 'isnotempty', filter: "date", type: "date" }, excludeAdverse ] };
          break;
        case 'Closed Loans':
          this.filter = { logic: 'and', filters: [ excludeCsllexclude, { field: 'f1393', operator: 'eq', value: 'Loan Originated' }, excludeAdverse ] };
          break;
        case 'Post Closing':
          this.filter = { logic: 'and', filters: [ excludeCsllexclude, { logic: 'or', filters: [ { field: 'currentmilestone', operator: 'eq', value: 'Post Closing' }, { field: 'currentmilestone', operator: 'eq', value: 'Note Delivery' } ] }, excludeAdverse ] };
          break;
        case 'Non-Active':
          this.filter = { logic: 'or', filters: [ { logic: 'and', filters: [ { field: 'f1393', operator: 'neq', value: 'Active Loan' }, { field: 'f1393', operator: 'neq', value: 'Loan Originated' } ] }, { field: 'CXLLEXCLUDE', operator: 'eq', value: true } ] };
          break;
        case 'Aged Loans':
          this.filter = { logic: 'and', filters: [ excludeCsllexclude, { field: 'f1393', operator: 'eq', value: 'Active Loan' }, { field: 'lastcompletedmilestonedate', operator: 'lte', value: cutoff, filter: "date", type: "date" }, excludeAdverse ] };
          break;
        case 'Aged Credit':
          this.filter = { logic: 'and', filters: [ excludeCsllexclude, { field: 'f1393', operator: 'eq', value: 'Active Loan' }, { field: 'creditreportorderdate', operator: 'lte', value: cutoff, filter: "date", type: "date" }, { field: 'currentmilestone', operator: 'eq', value: 'Started' }, excludeAdverse ] };
          break;
        case 'All':
        default:
          this.filter = { logic: 'and', filters: [ excludeCsllexclude ] };
      }
 
      this._viewFilter = JSON.parse(JSON.stringify(this.filter));
      this.$refs.ccTable?.resetFilters();
      this.fetchData();
    },

    clearSearch() {
      this.filter       = { logic: 'and', filters: [] };
      this.searchWord   = '';
      this.searchFilter = null;
      this.skip         = 0;
      this.fetchData();
    },
  },
};
</script>

<style scoped>
@import '/src/assets/css/defaultSliderInputs.css';

.polly-pricer-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #123a71;
  background: #eef3fb;
  border: 1px solid #c9d8ee;
  border-radius: 5px;
  cursor: pointer;
  white-space: nowrap;
}
.polly-pricer-btn:hover {
  background: #dfe9f8;
}

.processor-pipeline {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: visible;
  min-width: 1000px;
}

.processor-pipeline__container {
  flex-grow: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
}

.processor-pipeline__loader-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0; right: 0; bottom: 0; left: 0;
  z-index: 100;
}

.view-container {
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
}

.view-container::after {
  content: "▾";
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 24px;
  color: #6b7a99;
  pointer-events: none;
}

.cc-view-select {
  width: 230px;
  height: 34px;
  border: 1px solid #dfe4ee;
  border-radius: 5px;
  background: #fff;
  padding: 0 28px 0 10px;
  font-size: 13px;
  color: #23314f;
  outline: none;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  flex-shrink: 0;
}

.view-description {
  font-size: 12px;
  color: #6b7a99;
  white-space: nowrap;
  font-style: italic;
}

/* Sub-toolbar */
.sub-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 10px;
  border-top: 1px solid #eaecf4;
  background: #f9fafc;
  flex-shrink: 0;
}

.mine-segment {
  display: inline-flex;
  border: 1px solid #dfe4ee;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}

.mine-segment__btn {
  height: 30px;
  padding: 0 16px;
  border: none;
  background: transparent;
  font-size: 12px;
  font-weight: 500;
  color: #6b7a99;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
}

.mine-segment__btn:not(:last-child) {
  border-right: 1px solid #dfe4ee;
}

.mine-segment__btn.active {
  background: #185fa5;
  color: #fff;
}

.mine-segment__btn:not(.active):hover {
  background: #f0f4fb;
  color: #23314f;
}

/* Gear button */
.gear-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid #dfe4ee;
  border-radius: 5px;
  background: #fff;
  cursor: pointer;
  color: #6b7a99;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.gear-btn:hover {
  border-color: #aab4cc;
  color: #23314f;
}

.gear-btn.has-filters {
  border-color: #f0997b;
  background: #faece7;
  color: #993c1d;
}

.gear-btn .filter-count-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  background: #d85a30;
  color: #fff;
  border-radius: 10px;
  padding: 1px 5px;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.5;
}

/* Search bar */
.search-bar-container {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

.cc-search-input {
  width: 230px;
  height: 34px;
  border: 1px solid #999;
  border-radius: 5px;
  padding: 0 10px;
  font-size: 13px;
  color: #23314f;
  outline: none;
}

.clear-button {
  padding: 6px 12px;
  border: none;
  background-color: #051d3a;
  color: white;
  cursor: pointer;
  border-radius: 4px;
  font-size: 13px;
}
.clear-button:hover { background-color: #d32f2f; }

/* Advanced Filter Modal */
.adv-filter-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.adv-filter-modal {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #dfe4ee;
  width: 680px;
  max-width: 95vw;
  padding: 22px 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.adv-filter-title {
  font-size: 15px;
  font-weight: 600;
  color: #23314f;
  margin-bottom: 16px;
}

.adv-filter-rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
  min-height: 36px;
}

.adv-filter-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.logic-badge {
  width: 46px;
  height: 28px;
  border-radius: 5px;
  border: 1px solid #dfe4ee;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  letter-spacing: 0.03em;
}

.logic-and { background: #e6f1fb; border-color: #85b7eb; color: #185fa5; }
.logic-or  { background: #fff6e5; border-color: #ef9f27; color: #854f0b; }

.logic-spacer { width: 46px; flex-shrink: 0; }

.adv-select {
  height: 30px;
  border: 1px solid #dfe4ee;
  border-radius: 5px;
  font-size: 12px;
  padding: 0 8px;
  background: #fff;
  color: #23314f;
  outline: none;
  cursor: pointer;
}

.adv-select--field { width: 165px; flex-shrink: 0; }
.adv-select--op    { width: 135px; flex-shrink: 0; }

.adv-input {
  flex: 1;
  height: 30px;
  border: 1px solid #dfe4ee;
  border-radius: 5px;
  font-size: 12px;
  padding: 0 8px;
  color: #23314f;
  outline: none;
  min-width: 0;
}

.adv-input--placeholder {
  background: #f4f6f8;
  border-color: #eaecf1;
}

.adv-rm-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: none;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  color: #8a99b3;
  border-radius: 4px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s, color 0.12s;
}
.adv-rm-btn:hover { background: #fdebec; color: #9f2d2f; }

.adv-add-btn {
  height: 28px;
  padding: 0 14px;
  border: 1px dashed #c5cede;
  border-radius: 5px;
  background: none;
  color: #6b7a99;
  cursor: pointer;
  font-size: 12px;
  transition: border-color 0.12s, color 0.12s;
}
.adv-add-btn:hover { border-color: #85b7eb; color: #185fa5; }

.adv-modal-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #eaecf4;
  align-items: center;
}

.adv-apply-btn {
  height: 34px;
  padding: 0 18px;
  background: #051d3a;
  color: #fff;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.12s;
}
.adv-apply-btn:hover { background: #0a3262; }

.adv-cancel-btn {
  height: 34px;
  padding: 0 16px;
  background: none;
  border: 1px solid #dfe4ee;
  border-radius: 5px;
  cursor: pointer;
  font-size: 13px;
  color: #23314f;
  transition: border-color 0.12s;
}
.adv-cancel-btn:hover { border-color: #aab4cc; }

.adv-clear-btn {
  height: 34px;
  padding: 0 16px;
  background: none;
  border: 1px solid #f8d1d3;
  color: #9f2d2f;
  border-radius: 5px;
  cursor: pointer;
  font-size: 13px;
  margin-right: auto;
  transition: background 0.12s;
}
.adv-clear-btn:hover { background: #fdebec; }

/* Tasks header */
.tasks-header-cell { width: 100%; height: 100%; }

.header-summary {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.8rem;
  padding: 0.35rem 0.5rem;
}

.header-summary--tight { gap: 0.5rem; }
.header-pie { flex-shrink: 0; transform: rotate(-90deg); }

.header-pills {
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  gap: 0.6rem;
}

/* Pills */
.summary-pill,
.task-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 1.3rem;
  line-height: 1.25;
  white-space: nowrap;
  border: 1px solid transparent;
  box-shadow: 0 0 0 1px rgba(0,0,0,0.04) inset;
  min-width: 36px;
  cursor: pointer;
}

.summary-pill { cursor: pointer; }
.task-pill    { cursor: default; }

.pill-icon { opacity: 0.8; font-size: 1.2rem; }
.pill-num  { font-weight: 600; }

.ok      { background: #e9f7ef; color: #1e7f4f; border-color: #d2f0df; min-width: 36px; }
.warn    { background: #fff6e5; color: #9a6b00; border-color: #fde9c7; }
.danger  { background: #fdebec; color: #9f2d2f; border-color: #f8d1d3; }

.task-pills {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 0.5rem;
  margin-left: 3.95rem;
}

.neutral,
.celebratory {
  margin-left: 3.95rem;
  padding-left: 1rem;
  font-size: 1.3rem;
}

.neutral { background: #f4f6f8; color: #5b6770; border-color: #e6eaef; }

.celebratory {
  background: linear-gradient(135deg, #e8fff3, #f3fff1);
  color: #2f7a3e;
  border-color: #ccf2da;
  position: relative;
}
.celebratory::after {
  content: "✨";
  margin-left: 0.1rem;
  opacity: 0.9;
  font-size: 1.3rem;
}

/* Mobile */
@media (max-width: 480px) {
  .processor-pipeline { min-width: 0; }
  .search-bar-container { width: 100%; }
  .cc-search-input, .cc-view-select { width: 100%; }
  .view-description { display: none; }
  .sub-toolbar { display: none; }
}
</style>